import { ExportImportService } from '@admin/pages/export-import/export-import.service';
import { Injectable } from '@angular/core';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '@env/environment';
import { IQuestionCategory } from '@meeting/models/meeting-question-category.model';
import { ETypes } from '@meeting/models/meeting-type.interface';
import {
  EMeetingStatuses,
  EParticipantRoles,
  IMeetingResult,
  IParticipant,
  IPrepareMeetingResult,
} from '@meeting/models/meeting.model';
import { CampaignsService } from '@meeting/services/campaigns.service';
import { MeetingCategoriesService } from '@meeting/services/meeting-category.service';
import { MeetingQuestionsService } from '@meeting/services/meeting-question.service';
import { MeetingTemplatesService } from '@meeting/services/meeting-template.service';
import { MeetingTypesService } from '@meeting/services/meeting-type.service';
import {
  ISendDownloadedReportEventPayload,
  MeetingService,
} from '@meeting/services/meeting.service';
import { Company } from '@models/company.model';
import { User } from '@models/user.model';
import posthog, { Properties } from 'posthog-js';
import { EMPTY, Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthenticationService } from './authentication.service';
import { CompanyService } from './company.service';
import { FilesService } from './files.service';

enum Event {
  USER = 'User',
  MEETING = 'Meeting',
  TEMPLATE = 'Template',
  QUESTION = 'Question',
  INTERVIEW_REPORT = 'Interview Report',
  CAMPAIGN_REPORT = 'Campaign Reports',
  CAMPAIGN_XLS_EXPORT = 'Campaign XLS Exports',
  CAMPAIGN = 'Campaign',
}

export enum EDataMutation {
  CREATED = 'Created',
  STARTED = 'Started',
  MODIFIED = 'Modified',
  DELETED = 'Deleted',
  ADD_PARTICIPANTS = 'Add Participants',
  EXTEND_END_DATE = 'Extend Date',
  REMINDER_SENT = 'Reminder sent',
}

export enum EMeetingMutation {
  PREPARED = 'Prepared',
  COMPLETED = 'Completed',
  CLOSED = 'Closed',
  DRAFT = 'Draft',
  CHANGE_REVIEWER = 'Change Reviewer',
  DELETED = 'Deleted',
}

enum EUserRole {
  ADMIN = 'admin',
  USER = 'user',
}

enum EAccessOrigin {
  MY_SILAE = 'my silae',
  EMAIL = 'email',
  DIRECT = 'direct',
}

export enum EFileType {
  CAMPAIGN_REPORT,
  MEETING_EXPORT,
  CAMPAIGN_XLSCampaignExports,
}

export enum EImportSteps {
  METHOD_SELECTED = 'Import Method Selected',
  DOC_UPLOADED = 'Import Document Uploaded',
  IMPORT_COMPTED = 'Import Completed',
}

export interface ILoggableData {
  fileType: EFileType;
  data?: { meetingId?: string; campaignId?: string };
}

@Injectable({ providedIn: 'root' })
export class PostHogService {
  private apiKey = environment.POST_HOG_API_KEY;
  private apiHost = environment.POST_HOG_HOST;

  public emitAccessEvent = new Subject<void>();

  constructor(
    private readonly _meetingService: MeetingService,
    private readonly _router: Router,
    private readonly _jwt: JwtHelperService,
    private readonly _authService: AuthenticationService,
    private readonly _companyService: CompanyService,
    private readonly _meetingCategoriesService: MeetingCategoriesService,
    private readonly _meetingTypesService: MeetingTypesService,
    private readonly _campaignsService: CampaignsService,
    private readonly _meetingTemplatesService: MeetingTemplatesService,
    private readonly _meetingQuestionsService: MeetingQuestionsService,
    private readonly _filesService: FilesService,
    private readonly exportImportService: ExportImportService,
  ) {
    if (!environment.POST_HOG_ENABLED) {
      return;
    }

    this.initPostHog();

    this.initAccessEvent();

    this.initQuestionEvent();
    this.initTemplateEvent();

    this.initMeetingEvent();
    this.initDownloadedReportEvent();

    this.initCampaignEvents();
    this.initExtendCampaignDateEvent();
    this.initAddCampaignParticipantsEvent();
    this.initCampaignReminderSentEvent();
    this.initDownloadedCampaignReportsEvent();
    this.initDownloadedCampaignXLSExportEvent();

    this.initExportImportEvent();
  }

  private getUserPipeline(): Observable<User> {
    return this._authService.currentUser$.pipe(
      filter((user) => {
        return !!user;
      }),
    );
  }

  private getCompanyPipeline(): Observable<Company> {
    return this._companyService.currentCompany$.pipe(
      filter((company) => {
        return !!company;
      }),
    );
  }

  private initAccessEvent(): void {
    const routesRecognizedPipeline = this._router.events.pipe(
      filter((event) => {
        return event instanceof RoutesRecognized && event.id === 1;
      }),
      map((event) => {
        return event as RoutesRecognized;
      }),
    );

    const navigationEndPipeline = this._router.events.pipe(
      filter((event) => {
        return event instanceof NavigationEnd;
      }),
      map((event) => {
        return !!event;
      }),
    );

    this.emitAccessEvent
      .pipe(
        take(1),
        switchMap(() => {
          return combineLatest([
            this.getUserPipeline(),
            this.getCompanyPipeline(),
            routesRecognizedPipeline,
            navigationEndPipeline,
          ]).pipe(take(1));
        }),
      )
      .subscribe(([user, company, routesRecognized, isNavigationEnd]) => {
        if (!!routesRecognized && isNavigationEnd) {
          this.sendAccessEvent(user, company, routesRecognized);
        }
      });
  }

  private initQuestionEvent(): void {
    this._meetingQuestionsService.emitQuestionEvent
      .pipe(
        switchMap(({ mutation, question }) => {
          return combineLatest([
            of(mutation),
            this.getCompanyPipeline(),
            this._meetingCategoriesService.getCategoryById$(
              question.categoryId,
            ),
          ]);
        }),
      )
      .subscribe(([mutation, company, category]) => {
        this.sendQuestionEvent(mutation, category, company);
      });
  }

  private initTemplateEvent(): void {
    this._meetingTemplatesService.emitTemplateEvent
      .pipe(
        switchMap(({ mutation, template }) => {
          return combineLatest([
            of(mutation),
            of(template),
            this._meetingTypesService.getTypeById$(template.typeId),
            this.getCompanyPipeline(),
          ]);
        }),
      )
      .subscribe(([mutation, template, type, company]) => {
        this.sendTemplateEvent(
          mutation,
          {
            id: template.id,
            type: type.type,
            name: template.title.fr,
            updatedAt: template.updatedAt,
          },
          company,
        );
      });
  }

  private initCampaignEvents(): void {
    this._campaignsService.emitCampaignStatusChangeEvent
      .pipe(
        switchMap(({ mutation, campaign, isStartTriggeredManually }) => {
          return this.getCompanyPipeline().pipe(
            map((company) => {
              return { mutation, campaign, isStartTriggeredManually, company };
            }),
          );
        }),
        tap(({ mutation, campaign, isStartTriggeredManually, company }) => {
          this.sendCampaignEvent(
            mutation,
            {
              campaignId: campaign.id,
              totalMeetings: campaign.meetings?.count ?? null,
              isStartTriggeredManually,
            },
            company,
          );
        }),
        switchMap(
          ({ mutation, campaign, isStartTriggeredManually, company }) => {
            const meetingPipeline: Observable<IMeetingResult[]> =
              Array.isArray(campaign.meetings?.ids) &&
              campaign.meetings?.ids.length > 0
                ? this._meetingService
                    .getMeetingsWithFilters$({
                      ids: campaign.meetings?.ids,
                    })
                    .pipe(
                      map((meeting) => {
                        return meeting.data;
                      }),
                    )
                : of([]);

            return combineLatest([
              of(mutation),
              of(campaign),
              of(isStartTriggeredManually),
              of(company),
              meetingPipeline,
              this.getUserPipeline(),
            ]);
          },
        ),
      )
      .subscribe(
        ([
          mutation,
          campaign,
          isStartTriggeredManually,
          company,
          meetings,
          user,
        ]) => {
          if (
            (mutation === EDataMutation.CREATED ||
              (mutation === EDataMutation.STARTED &&
                !isStartTriggeredManually)) &&
            meetings?.length > 0 &&
            meetings[0].config?.canViewRevieweeAnswers
          ) {
            this.sendToggleRevieweeResponseVisibleActivatedEvent(
              Event.CAMPAIGN,
              campaign.id,
              company,
            );
          }

          for (const meeting of meetings) {
            this.sendMeetingEvent(
              EDataMutation.CREATED,
              {
                campaignId: campaign.id,
                startDate: meeting.startDate,
                endDate: meeting.endDate,
                status: meeting.status,
                meetingId: meeting.id,
                templateName: campaign.title,
                signatureStatus: '',
                userRole: this.getUserRole(user),
                participantRole: '',
                canViewRevieweeAnswers: meeting.config?.canViewRevieweeAnswers,
              },
              company,
            );
          }
        },
      );
  }

  private initMeetingEvent(): void {
    this._meetingService.emitMeetingEvent
      .pipe(
        switchMap(({ mutation, meeting, isStartTriggeredManually }) => {
          return combineLatest([
            of(mutation),
            of(meeting),
            of(isStartTriggeredManually),
            this.getUserPipeline(),
            this.getCompanyPipeline(),
            this._meetingTemplatesService.getTemplateById$(meeting.templateId),
          ]);
        }),
      )
      .subscribe(
        ([
          mutation,
          meeting,
          isStartTriggeredManually,
          user,
          company,
          template,
        ]) => {
          const participant = this.isPrepareMeeting(meeting)
            ? meeting.participant
            : meeting.participants.find(
                (p) => (p.userId ?? p.user?.id) === user.id,
              );

          this.sendMeetingEvent(
            mutation,
            {
              participantRole: this.getParticipantRole(participant),
              signatureStatus: this.getParticipantSignatureStatus(participant),
              templateName: template?.title?.fr,
              startDate: meeting.startDate,
              endDate: meeting.endDate,
              userRole: this.getUserRole(user),
              meetingId: meeting.id,
              status: meeting.status,
              campaignId: meeting.campaignId,
              canViewRevieweeAnswers: meeting.config?.canViewRevieweeAnswers,
              isStartTriggeredManually,
            },
            company,
          );

          if (
            mutation === EDataMutation.CREATED &&
            meeting.config?.canViewRevieweeAnswers
          ) {
            this.sendToggleRevieweeResponseVisibleActivatedEvent(
              Event.MEETING,
              meeting.id,
              company,
            );
          }
        },
      );
  }

  private getNbMeetingFinishedFromCampaign(
    campaignId: string,
  ): Observable<number> {
    return this._meetingService
      .getMeetingsWithFilters$({
        campaignIds: [campaignId],
        statuses: [EMeetingStatuses.CLOSED_BY_BOTH],
      })
      .pipe(
        take(1),
        switchMap((result) => {
          return of(result.pagination.total);
        }),
      );
  }

  private initDownloadedCampaignReportsEvent(): void {
    this._filesService.emitDownloadedFileEvent
      .pipe(
        filter((dataToLog) => {
          return dataToLog.fileType === EFileType.CAMPAIGN_REPORT;
        }),
        switchMap((dataToLog) => {
          return combineLatest([
            this.getNbMeetingFinishedFromCampaign(dataToLog.data.campaignId),
            this.getCompanyPipeline(),
          ]);
        }),
      )
      .subscribe(([nbDocInZip, company]) => {
        this.sendDownloadedCampaignReportsEvent(company, nbDocInZip);
      });
  }

  private initDownloadedCampaignXLSExportEvent(): void {
    this._filesService.emitDownloadedFileEvent
      .pipe(
        filter((dataToLog) => {
          return dataToLog.fileType === EFileType.CAMPAIGN_XLSCampaignExports;
        }),
        switchMap(() => {
          return this.getCompanyPipeline();
        }),
      )
      .subscribe((company) => {
        this.sendDownloadedCampaignXLSExportEvent(company);
      });
  }

  private initDownloadedReportEvent(): void {
    this._filesService.emitDownloadedFileEvent
      .pipe(
        filter((dataToLog) => {
          return dataToLog.fileType === EFileType.MEETING_EXPORT;
        }),
        switchMap((dataToLog) => {
          if (!dataToLog.data?.meetingId) {
            return EMPTY;
          }
          return this._meetingService.getOne(dataToLog.data.meetingId);
        }),
        switchMap((meeting) => {
          return combineLatest([
            this.getUserPipeline(),
            of(meeting),
            this.getCompanyPipeline(),
          ]);
        }),
      )
      .subscribe(([user, meeting, company]) => {
        const participant = meeting.participants.find(
          (p) => p.user.id === user.id,
        );

        this.sendDownloadedReportEvent({ participant, company });
      });
  }

  private initExtendCampaignDateEvent(): void {
    this._campaignsService.emitCampaignExtendDateEvent
      .pipe(
        switchMap((campaignId) => {
          return this.getCompanyPipeline().pipe(
            map((company) => {
              return { campaignId, company };
            }),
          );
        }),
      )
      .subscribe(({ campaignId, company }) => {
        this.sendCampaignExtendDateEvent(campaignId, company);
      });
  }

  private initCampaignReminderSentEvent(): void {
    this._campaignsService.emitCampaignReminderSentEvent
      .pipe(
        switchMap((campaignId) => {
          return this.getCompanyPipeline().pipe(
            map((company) => {
              return { campaignId, company };
            }),
          );
        }),
      )
      .subscribe(({ campaignId, company }) => {
        this.sendCampaignReminderSentEvent(campaignId, company);
      });
  }

  private initAddCampaignParticipantsEvent(): void {
    this._campaignsService.emitAddCampaignParticipantsEvent
      .pipe(
        switchMap(({ campaignId, createdMeetingIds }) => {
          return this.getCompanyPipeline().pipe(
            map((company) => {
              return { campaignId, createdMeetingIds, company };
            }),
          );
        }),
        tap(({ campaignId, createdMeetingIds, company }) => {
          this.sendCampaignAddParticipantsEvent(
            campaignId,
            createdMeetingIds,
            company,
          );
        }),
        switchMap(({ campaignId, createdMeetingIds, company }) => {
          const meetingPipeline: Observable<IMeetingResult[]> =
            Array.isArray(createdMeetingIds) && createdMeetingIds.length > 0
              ? this._meetingService
                  .getMeetingsWithFilters$({
                    ids: createdMeetingIds,
                  })
                  .pipe(
                    map((meeting) => {
                      return meeting.data;
                    }),
                  )
              : of([]);

          return combineLatest([
            this._campaignsService.getOneCampaign$(campaignId),
            meetingPipeline,
            of(company),
            this.getUserPipeline(),
          ]);
        }),
      )
      .subscribe(([campaign, meetings, company, user]) => {
        for (const meeting of meetings) {
          this.sendMeetingEvent(
            EDataMutation.CREATED,
            {
              campaignId: campaign.id,
              startDate: meeting.startDate,
              endDate: meeting.endDate,
              status: meeting.status,
              meetingId: meeting.id,
              templateName: campaign.title,
              signatureStatus: '',
              userRole: this.getUserRole(user),
              participantRole: '',
              canViewRevieweeAnswers: meeting.config?.canViewRevieweeAnswers,
            },
            company,
          );
        }
      });
  }

  private initExportImportEvent(): void {
    this.exportImportService.emitExportEvent
      .pipe(
        switchMap((time) => {
          return this.getCompanyPipeline().pipe(
            map((company) => {
              return { time, company };
            }),
          );
        }),
      )
      .subscribe(({ time, company }) => {
        this.sendExportEvent(company, time);
      });

    this.exportImportService.emitImportEvent
      .pipe(
        switchMap((dataToLog) => {
          return combineLatest([
            this.getUserPipeline(),
            this.getCompanyPipeline(),
          ]).pipe(
            take(1),
            map(([user, company]) => {
              return { ...dataToLog, user, company };
            }),
          );
        }),
      )
      .subscribe(({ user, company, importId, importType, step }) => {
        this.sendImportEvent(user, company, importId, importType, step);
      });
  }

  private initPostHog() {
    if (this.apiKey) {
      posthog.init(this.apiKey, {
        api_host: this.apiHost,
        autocapture: true,
        name: 'Talent',
      });
    }
  }

  private sendAccessEvent(
    user: User,
    company: Company,
    routesRecognized: RoutesRecognized,
  ): void {
    posthog.identify(user.email.toLocaleLowerCase(), {
      email: user.email.toLocaleLowerCase(),
      firstname: user.firstname,
      lastname: user.lastname,
    });

    posthog.capture(`${Event.USER} Accessed Talent`, {
      origin: this.getUserOrigin(
        routesRecognized?.state?.root?.firstChild?.queryParams,
      ),
      user_role: this.getUserRole(user),
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    });
  }

  private sendQuestionEvent(
    mutation: EDataMutation,
    category: IQuestionCategory,
    company: Company,
  ): void {
    posthog.capture(`${Event.QUESTION} ${mutation}`, {
      category: category.name.fr,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    });
  }

  private sendTemplateEvent(
    mutation: EDataMutation,
    template: {
      id: string;
      type: ETypes;
      name: string;
      updatedAt: Date;
    },
    company: Company,
  ): void {
    posthog.capture(`${Event.TEMPLATE} ${mutation}`, {
      template_id: template.id,
      template_type: template.type,
      template_name: template.name,
      updated_at: template.updatedAt ?? '',
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    });
  }

  private sendMeetingEvent(
    mutation: EDataMutation | EMeetingMutation,
    data: {
      templateName: string;
      signatureStatus: 'approved' | 'rejected' | '';
      startDate: Date;
      endDate: Date;
      userRole: 'user' | 'admin';
      participantRole: string;
      meetingId: string;
      status: EMeetingStatuses;
      campaignId: string | null;
      canViewRevieweeAnswers?: boolean;
      isStartTriggeredManually?: boolean;
    },
    company: Company,
  ): void {
    const posthogData: { [key: string]: string | number | boolean | Date } = {
      template_name: data.templateName,
      signature_status: data.signatureStatus,
      start_date: data.startDate,
      end_date: data.endDate,
      user_role: data.userRole,
      participant_role: data.participantRole,
      meeting_id: data.meetingId,
      meeting_status: data.status.toLowerCase(),
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      campaign_id: data.campaignId,
      platform_id: this.extractHostFromPlatform(company?.platform),
      can_view_reviewee_answers: data.canViewRevieweeAnswers,
    };

    if (
      mutation === EDataMutation.STARTED &&
      data?.isStartTriggeredManually !== null
    ) {
      posthogData.triggered_manually = data.isStartTriggeredManually;
    }

    posthog.capture(`${Event.MEETING} ${mutation}`, posthogData);
  }

  private sendToggleRevieweeResponseVisibleActivatedEvent(
    meetingOrCampaign: Event.MEETING | Event.CAMPAIGN,
    id: string,
    company: Company,
  ): void {
    const pothogData: { [key: string]: string | number } = {
      domain_id: company?.domaineGuid,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      platform_id: this.extractHostFromPlatform(company?.platform),
    };

    if (meetingOrCampaign === Event.MEETING) {
      pothogData.meeting_id = id;
    } else {
      pothogData.campaign_id = id;
    }

    posthog.capture(
      `${meetingOrCampaign} Preparation View Toggled`,
      pothogData,
    );
  }

  private sendCampaignEvent(
    mutation: EDataMutation,
    data: {
      campaignId: string;
      totalMeetings: number;
      isStartTriggeredManually?: boolean;
    },
    company: Company,
  ): void {
    const posthogData: { [key: string]: string | number | boolean } = {
      campaign_id: data.campaignId,
      total_meetings: data.totalMeetings,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    };

    if (
      mutation === EDataMutation.STARTED &&
      data?.isStartTriggeredManually !== null
    ) {
      posthogData.triggered_manually = data.isStartTriggeredManually;
    }

    posthog.capture(`${Event.CAMPAIGN} ${mutation}`, posthogData);
  }

  private sendCampaignExtendDateEvent(
    campaignId: string,
    company: Company,
  ): void {
    posthog.capture(`${Event.CAMPAIGN} ${EDataMutation.EXTEND_END_DATE}`, {
      campaign_id: campaignId,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    });
  }

  private sendCampaignReminderSentEvent(
    campaignId: string,
    company: Company,
  ): void {
    posthog.capture(`${Event.CAMPAIGN} ${EDataMutation.REMINDER_SENT}`, {
      campaign_id: campaignId,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    });
  }

  private sendCampaignAddParticipantsEvent(
    campaignId: string,
    createdMeetingIds: string[],
    company: Company,
  ): void {
    posthog.capture(`${Event.CAMPAIGN} ${EDataMutation.ADD_PARTICIPANTS}`, {
      campaign_id: campaignId,
      created_meetings: createdMeetingIds.length,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
    });
  }

  private sendDownloadedReportEvent(
    payload: ISendDownloadedReportEventPayload,
  ): void {
    if (!payload) return;
    const action = 'Downloaded';

    posthog.capture(`${Event.INTERVIEW_REPORT} ${action}`, {
      participant_role: this.getParticipantRole(payload.participant),
      scope: EUserRole.ADMIN,
      company_id: payload.company?.externalId,
      domain_id: payload.company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(payload.company?.platform),
      batch_download: false,
      dowloaded_doc_count: 1,
    });
  }

  private sendDownloadedCampaignReportsEvent(
    company: Company,
    nbDocInZip: number,
  ): void {
    const action = 'Downloaded';
    posthog.capture(`${Event.CAMPAIGN_REPORT} ${action}`, {
      participant_role: EParticipantRoles.ALL,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
      batch_download: true,
      dowloaded_doc_count: nbDocInZip,
    });
  }

  private sendDownloadedCampaignXLSExportEvent(company: Company): void {
    const action = 'Downloaded';
    posthog.capture(`${Event.CAMPAIGN_XLS_EXPORT} ${action}`, {
      participant_role: EParticipantRoles.ALL,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      domain_id: company?.domaineGuid,
      platform_id: this.extractHostFromPlatform(company?.platform),
      batch_download: false,
      dowloaded_doc_count: 1,
    });
  }

  private sendExportEvent(company: Company, time: number): void {
    posthog.capture('Export Completed', {
      export_time: time,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      platform_id: this.extractHostFromPlatform(company?.platform),
      domain_id: company?.domaineGuid,
      page_name: 'export',
    });
  }

  private sendImportEvent(
    user: User,
    company: Company,
    importId: string,
    importType: string,
    step: EImportSteps,
  ): void {
    const eventToSend: Properties = {
      import_id: `import-${user.id}-${importId}`,
      scope: EUserRole.ADMIN,
      company_id: company?.externalId,
      platform_id: this.extractHostFromPlatform(company?.platform),
      domain_id: company?.domaineGuid,
      page_name: 'import',
    };

    if (step === EImportSteps.IMPORT_COMPTED) {
      eventToSend.import_type = importType;
    }

    posthog.capture(step, eventToSend);
  }

  private getUserRole(user: User): EUserRole {
    if (user.role.permissions.includes('admin:access')) {
      return EUserRole.ADMIN;
    }
    return EUserRole.USER;
  }

  private getParticipantRole(participant?: IParticipant): string {
    const role = participant?.role;

    if (!role) {
      return '';
    }

    if (role === EParticipantRoles.ALL) {
      return '';
    }

    return role.toLowerCase();
  }

  private getParticipantSignatureStatus(
    participant?: IParticipant,
  ): 'approved' | 'rejected' | '' {
    if (!participant) {
      return '';
    }

    if (participant.signed) {
      return 'approved';
    }

    if (!participant.signed && !!participant.unSignedComment) {
      return 'rejected';
    }

    return '';
  }

  private isPrepareMeeting(
    meeting: IMeetingResult | IPrepareMeetingResult,
  ): meeting is IPrepareMeetingResult {
    return 'participant' in meeting;
  }

  private getUserOrigin(queryParams: object): EAccessOrigin {
    if (!queryParams || !this.isAccessLink(queryParams)) {
      return EAccessOrigin.DIRECT;
    }

    const token = this._jwt.decodeToken(
      queryParams['connectionContent[accessToken]'],
    );

    if (token?.iss?.includes('talent')) {
      return EAccessOrigin.EMAIL;
    }

    if (token?.iss?.includes('my.silae')) {
      return EAccessOrigin.MY_SILAE;
    }

    return EAccessOrigin.DIRECT;
  }

  private isAccessLink(queryParams: object): queryParams is {
    connectionMethod: string;
    'connectionContent[accessToken]': string;
  } {
    return (
      'connectionMethod' in queryParams &&
      'connectionContent[accessToken]' in queryParams
    );
  }

  private extractHostFromPlatform(platform: string): string {
    if (!platform) {
      return platform;
    }

    try {
      return new URL(platform)?.host;
    } catch {
      return platform;
    }
  }
}
